import { useFragment, graphql } from 'react-relay';
import { useState, useEffect, useMemo } from 'react';

// types
import {
    type useDefaultSkuId_viewer$key as Viewer,
    type useDefaultSkuId_viewer$data as ViewerData,
} from './__generated__/useDefaultSkuId_viewer.graphql';

type TransactionEdges = NonNullable<ViewerData['transactions']>['edges'];
type TransactionNode = NonNullable<NonNullable<TransactionEdges>[number]>['node'];

const viewerFragment = graphql`
    fragment useDefaultSkuId_viewer on Viewer {
        itemRead(itemIds: $itemIds) {
            ... on Item {
                isMultiSku
                defaultSkuId
                skus {
                    skuId
                }
            }
        }
        ... @include(if: $isMultiSku) {
            transactions(itemId: $itemIds, buyerId: $userIds) {
                edges {
                    node {
                        skuId
                        isPrivateOffer
                        isCanceled
                    }
                }
            }
        }
    }
`;

const getPrivateOffer = (transactionEdges: TransactionEdges): TransactionNode => {
    const foundTransaction = (transactionEdges || []).find(
        transaction =>
            transaction &&
            transaction.node &&
            transaction.node.isPrivateOffer &&
            !transaction.node.isCanceled
    ) || { node: null };

    return foundTransaction?.node;
};

export const useDefaultSkuId = ({
    viewer: viewerRef,
}: {
    viewer: Viewer;
}): { defaultSkuId: string | null } => {
    const [defaultSkuId, setDefaultSkuId] = useState<string | null>(null);
    const viewer = useFragment(viewerFragment, viewerRef);

    useEffect(() => {
        const item = viewer?.itemRead?.[0] || {};
        if (item?.isMultiSku) {
            const transactions = viewer?.transactions?.edges || [];
            const privateOfferSkuId = getPrivateOffer(transactions)?.skuId || null;
            const itemDefaultSkuId = item?.defaultSkuId || null;
            let skuIdFromUrl = window.location.hash.match(/skuId=(\w+)/)?.[1] || null;
            skuIdFromUrl = item?.skus?.find(sku => sku?.skuId === skuIdFromUrl)?.skuId || null;
            setDefaultSkuId(skuIdFromUrl || privateOfferSkuId || itemDefaultSkuId);
        }
    }, [viewer]);

    return useMemo(() => ({ defaultSkuId }), [defaultSkuId]);
};
